import Button from 'components/Button'
import FormRow from 'components/Form/FormRow'
import { Combobox } from 'components/Form/Inputs/Combobox'
import InputField from 'components/Form/Inputs/InputField'
import SelectField from 'components/Form/Inputs/SelectField'
import SubmitButton from 'components/Form/SubmitButton'
import ModalBody from 'components/Modal/ModalBody'
import ModalFooter from 'components/Modal/ModalFooter'
import UrlModal from 'components/Modal/UrlModal'
import { useGetItemsQuery } from 'containers/items/queries'
import { useQuantityUnitsQuery } from 'containers/quantityUnits/queries'
import { UpdateRecipeItemQuantityPayload } from 'containers/recipes/types'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useGetSingleRecipeQuery, useUpdateRecipeItemQuantityMutation } from '../queries'

type Inputs = {
    ingredientName: string
    quantity: string
    quantityUnitId: string
    itemGroup: string
}

export function UpdateRecipeItemQuantityForm() {
    const navigate = useNavigate()
    const { recipeId, recipeItemId } = useParams()

    const { data: quantityUnitsData, isFetching: isQuantityUnitsFetching, isError: isQuantityUnitsError } = useQuantityUnitsQuery()
    const { data: singleRecipeData, isFetching: isSingleRecipeFetching, isError: isSingleRecipeError } = useGetSingleRecipeQuery(recipeId || '')
    const { data: itemsData } = useGetItemsQuery()

    const { mutateAsync: updateRecipeItemQuantity } = useUpdateRecipeItemQuantityMutation()

    const recipeItems = singleRecipeData?.items || []
    const foundListItem = recipeItems.find(({ recipe_item_id }) => recipe_item_id === Number(recipeItemId))

    const itemGroupNames = [
        ...new Set(
            recipeItems.reduce((acc: string[], curr) => {
                const group = curr.item_group
                return group ? [...acc, group] : acc
            }, [])
        )
    ]

    const methods = useForm<Inputs>({
        mode: 'onChange',
        defaultValues: {
            ingredientName: foundListItem?.name || '',
            quantity: foundListItem?.item_quantity.quantity.toString() || '',
            quantityUnitId: foundListItem?.item_quantity.quantity_unit?.id.toString() || 'NO_UNIT',
            itemGroup: foundListItem?.item_group || ''
        }
    })

    const {
        handleSubmit,
        formState: { isValid, isSubmitting, isDirty }
    } = methods

    const onSubmit: SubmitHandler<Inputs> = async ({ quantity, quantityUnitId, itemGroup, ingredientName }) => {
        const quanityUnitIdToSend = quantityUnitId === 'NO_UNIT' ? null : Number(quantityUnitId)
        const attributes: UpdateRecipeItemQuantityPayload = {
            recipe_item_id: Number(recipeItemId),
            quantity: Number(quantity),
            quantity_unit_id: quanityUnitIdToSend,
            item_group: itemGroup,
            ingredient_name: ingredientName
        }

        await updateRecipeItemQuantity(
            { recipeId: recipeId || '', attributes },
            {
                onSuccess() {
                    navigate(-1)
                }
            }
        )
    }

    const renderForm = () => {
        if (isSingleRecipeError || isQuantityUnitsError) return <h2>Error fetching data!</h2>
        if (!singleRecipeData || !quantityUnitsData) return ''

        return (
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <ModalBody>
                        <FormRow>
                            <Combobox.HookForm
                                className='w-full'
                                label='Ingredient Name'
                                name='ingredientName'
                                options={(itemsData ?? []).map((item) => {
                                    return {
                                        id: item.id,
                                        value: item.name
                                    }
                                })}
                            />
                        </FormRow>
                        <FormRow>
                            <InputField.HookForm label='Quantity' name='quantity' type='number' />
                        </FormRow>
                        <FormRow>
                            <SelectField.HookForm
                                label='Unit'
                                name='quantityUnitId'
                                options={[
                                    {
                                        label: 'no unit',
                                        value: 'NO_UNIT'
                                    },
                                    ...(quantityUnitsData?.map((quantityUnit) => ({
                                        label: quantityUnit.symbol,
                                        value: quantityUnit.id.toString(10)
                                    })) || [])
                                ]}
                            />
                        </FormRow>
                        <FormRow className='mb-40'>
                            <Combobox.HookForm
                                className='w-full'
                                label='Ingredient Group'
                                name='itemGroup'
                                options={itemGroupNames.map((name, index) => ({
                                    id: index,
                                    value: name
                                }))}
                            />
                        </FormRow>
                    </ModalBody>
                    <ModalFooter
                        buttons={[
                            <Button key={1} color='secondary' onClick={() => navigate(-1)}>
                                Cancel
                            </Button>,
                            <SubmitButton key={2} isSubmitting={isSubmitting} isValid={isValid} isDirty={isDirty} text='Save' />
                        ]}
                    />
                </form>
            </FormProvider>
        )
    }

    return (
        <div>
            <UrlModal
                title='Update Recipe Item'
                desc={foundListItem?.name}
                onClose={() => navigate(-1)}
                loading={isSingleRecipeFetching || isQuantityUnitsFetching}
            >
                <>{renderForm()}</>
            </UrlModal>
        </div>
    )
}
